/**
 * @fileoverview Note data source, type definitions, and visit tracking.
 */

import { NoteSchema, type Note } from '@schema/index';
import { apiClient } from './api-client';
import { createBaseDataSource } from './base-data-source';
import { createAccountTrackingFields, createArchivedAtField, createBooleanField, createCreatedAtField, createDateField, createDateTimeField, createDeletedAtField, createIdField, createJsonField, createNumberField, createSelectField, createStringField, createUpdatedAtField, FIELD_TYPE_WIDTHS, FIELD_WIDTHS } from './field-factories';
import { getCache } from './cache-registry';
import { createOptionLoader } from './option-loader';

export type { Note };

const personLoader = createOptionLoader('person');
const statusLoader = createOptionLoader('status');
const kindLoader = createOptionLoader('kind');
const projectLoader = createOptionLoader('project');
const resourceLoader = createOptionLoader('resource');
const areaLoader = createOptionLoader('area');
const categoryLoader = createOptionLoader('category');
const subcategoryLoader = createOptionLoader('subcategory');
const organisationLoader = createOptionLoader('organisation');
const parentNoteLoader = createOptionLoader('note', 'title', 'id', '(No Parent Note)');

export const noteDataSource = createBaseDataSource<Note>({
  endpoint: 'note',
  searchField: 'title',
  schema: NoteSchema,
  fields: [
    createIdField(),
    createStringField('title', 'Title', FIELD_TYPE_WIDTHS.NAME),
    createStringField('content', 'Content', FIELD_WIDTHS.XLARGE),
    createNumberField('version_number', 'Version', FIELD_WIDTHS.SMALL),
    createSelectField('person_id', 'Person', personLoader.getOptions(), FIELD_WIDTHS.MEDIUM),
    createStringField('priority', 'Priority', FIELD_WIDTHS.SMALL),
    createNumberField('cognitive_load', 'Cognitive Load', FIELD_WIDTHS.SMALL),
    createNumberField('progress', 'Progress', FIELD_WIDTHS.SMALL),
    createSelectField('status_id', 'Status', statusLoader.getOptions(), FIELD_WIDTHS.MEDIUM),
    createSelectField('kind_id', 'Kind', kindLoader.getOptions(), FIELD_WIDTHS.MEDIUM),
    createSelectField('project_id', 'Project', projectLoader.getOptions(), FIELD_WIDTHS.MEDIUM),
    createSelectField('resource_id', 'Resource', resourceLoader.getOptions(), FIELD_WIDTHS.MEDIUM),
    createSelectField('area_id', 'Area', areaLoader.getOptions(), FIELD_WIDTHS.MEDIUM),
    createSelectField('category_id', 'Category', categoryLoader.getOptions(), FIELD_WIDTHS.MEDIUM),
    createSelectField('subcategory_id', 'Subcategory', subcategoryLoader.getOptions(), FIELD_WIDTHS.MEDIUM),
    createSelectField('organisation_id', 'Organisation', organisationLoader.getOptions(), FIELD_WIDTHS.MEDIUM),
    createSelectField('parent_note_id', 'Parent Note', parentNoteLoader.getOptions(), FIELD_WIDTHS.MEDIUM),
    createDateField('start_date', 'Start Date', FIELD_TYPE_WIDTHS.DATE),
    createDateField('due_date', 'Due Date', FIELD_TYPE_WIDTHS.DATE),
    createDateTimeField('completed_at', 'Completed', FIELD_TYPE_WIDTHS.DATE),
    createNumberField('estimated_minutes', 'Est. Minutes', FIELD_WIDTHS.SMALL),
    createNumberField('story_points', 'Story Points', FIELD_WIDTHS.SMALL),
    createJsonField('metadata', 'Metadata', FIELD_WIDTHS.XLARGE),
    createStringField('search_vector', 'Search Vector', FIELD_WIDTHS.XLARGE),
    createStringField('embedding', 'Embedding', FIELD_WIDTHS.XLARGE),
    createBooleanField('is_pinned', 'Is Pinned', FIELD_WIDTHS.SMALL),
    createBooleanField('is_bookmarked', 'Is Bookmarked', FIELD_WIDTHS.SMALL),
    createBooleanField('is_flagged', 'Is Flagged', FIELD_WIDTHS.SMALL),
    createBooleanField('is_milestone', 'Is Milestone', FIELD_WIDTHS.SMALL),
    ...createAccountTrackingFields({ includeArchived: true, accountOptions: [] }),
    createArchivedAtField(),
    createDeletedAtField(),
    createCreatedAtField(),
    createUpdatedAtField(),
  ],
});

export const noteCache = getCache('note');

/**
 * Records a visit to a note (fire-and-forget).
 * Silently ignores errors to avoid impacting the user experience.
 *
 * @param noteId - The ID of the note being visited
 */
export function recordNoteVisit(noteId: string): void {
  apiClient.post(`note/${noteId}/visit`, {}).catch(() => {
    // Silently ignore errors - visit tracking is non-critical
  });
}

/** Lightweight shape returned by the similar-notes endpoint. */
export interface SimilarNote {
  id: string;
  title: string;
}

interface SimilarNotesResponse {
  items: SimilarNote[];
}

/**
 * Fetches the 5 nearest-neighbor notes by embedding cosine distance.
 *
 * @param noteId - The ID of the note to find similar notes for
 */
export async function getSimilarNotes(noteId: string): Promise<SimilarNote[]> {
  const data = await apiClient.get<SimilarNotesResponse>(`note/${noteId}/similar`);
  return data.items;
}

// =============================================================================
// Smart Tag Suggestions
// =============================================================================

/** A tag suggested for a note, with its centroid similarity score (0-1). */
export interface SuggestedTag {
  name: string;
  score: number;
}

interface SuggestedTagsResponse {
  items: SuggestedTag[];
}

/** Whether a suggestion was applied to the note or dismissed by the user. */
export type SuggestedTagAction = 'accept' | 'dismiss';

/**
 * Parses the `suggested_tags` array out of a note's metadata. Note metadata is
 * delivered from the API as a JSON string (or null); this safely extracts and
 * normalizes the suggested tags, discarding malformed entries.
 *
 * @param metadata - The note's raw metadata (JSON string, object, or null)
 */
export function parseSuggestedTags(metadata: unknown): SuggestedTag[] {
  let parsed: unknown = metadata;
  if (typeof metadata === 'string') {
    if (!metadata) return [];
    try {
      parsed = JSON.parse(metadata);
    } catch {
      return [];
    }
  }

  if (!parsed || typeof parsed !== 'object') return [];
  const value = (parsed as { suggested_tags?: unknown }).suggested_tags;
  if (!Array.isArray(value)) return [];

  return value.flatMap((entry): SuggestedTag[] => {
    if (entry && typeof entry === 'object' && typeof (entry as SuggestedTag).name === 'string') {
      const { name, score } = entry as SuggestedTag;
      return [{ name, score: typeof score === 'number' ? score : 0 }];
    }
    return [];
  });
}

/**
 * Whether a note's metadata contains the `suggested_tags` key at all. The
 * embedding worker writes this key (possibly as an empty array) once it has
 * processed the note, so its absence means suggestions are still being
 * computed.
 *
 * @param metadata - The note's raw metadata (JSON string, object, or null)
 */
export function hasSuggestedTagsKey(metadata: unknown): boolean {
  let parsed: unknown = metadata;
  if (typeof metadata === 'string') {
    if (!metadata) return false;
    try {
      parsed = JSON.parse(metadata);
    } catch {
      return false;
    }
  }

  if (!parsed || typeof parsed !== 'object') return false;
  return 'suggested_tags' in parsed;
}

/**
 * Resolves a smart tag suggestion by accepting or dismissing it. Returns the
 * remaining suggestions for the note.
 *
 * @param noteId - The note the suggestion belongs to
 * @param tag - The suggested tag name
 * @param action - 'accept' removes it from suggestions; 'dismiss' also records
 *                 it so it is never suggested again
 */
export async function resolveSuggestedTag(
  noteId: string,
  tag: string,
  action: SuggestedTagAction
): Promise<SuggestedTag[]> {
  const data = await apiClient.post<SuggestedTagsResponse>(`note/${noteId}/suggested-tags/resolve`, {
    tag,
    action,
  });
  return data.items;
}

// =============================================================================
// Quality Score
// =============================================================================

/** Score (0-10) for one quality component, with detected shortcomings. */
export interface QualityComponentScore {
  score: number;
  issues: string[];
}

/** Per-component quality breakdown stored in `metadata.quality_breakdown`. */
export interface QualityBreakdown {
  substance: QualityComponentScore;
  structure: QualityComponentScore;
  connectedness: QualityComponentScore;
  completeness: QualityComponentScore;
}

/** Keys of the four quality components, in display order. */
export const QUALITY_COMPONENT_KEYS = ['substance', 'structure', 'connectedness', 'completeness'] as const;

/** Coerces one stored component into a QualityComponentScore, or null. */
function toComponentScore(value: unknown): QualityComponentScore | null {
  if (!value || typeof value !== 'object') return null;
  const { score, issues } = value as { score?: unknown; issues?: unknown };
  if (typeof score !== 'number' || !Number.isFinite(score)) return null;
  return {
    score,
    issues: Array.isArray(issues) ? issues.filter((entry): entry is string => typeof entry === 'string') : [],
  };
}

/**
 * Parses the `quality_breakdown` object out of a note's metadata. Note
 * metadata is delivered from the API as a JSON string (or null); this safely
 * extracts and normalizes the breakdown, returning null when it is absent or
 * malformed (e.g. the worker has not scored the note yet).
 *
 * @param metadata - The note's raw metadata (JSON string, object, or null)
 */
export function parseQualityBreakdown(metadata: unknown): QualityBreakdown | null {
  let parsed: unknown = metadata;
  if (typeof metadata === 'string') {
    if (!metadata) return null;
    try {
      parsed = JSON.parse(metadata);
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== 'object') return null;
  const value = (parsed as { quality_breakdown?: unknown }).quality_breakdown;
  if (!value || typeof value !== 'object') return null;

  const components = QUALITY_COMPONENT_KEYS.map((key) =>
    toComponentScore((value as Record<string, unknown>)[key])
  );
  if (components.some((component) => component === null)) return null;

  const [substance, structure, connectedness, completeness] = components as QualityComponentScore[];
  return { substance, structure, connectedness, completeness };
}

// =============================================================================
// Bidirectional Linking
// =============================================================================

/** Lightweight shape for backlink/forward-link items. */
export interface LinkedNote {
  id: string;
  title: string;
}

interface LinkedNotesResponse {
  items: LinkedNote[];
}

/**
 * Fetches notes that link TO the given note (backlinks / reverse lookup).
 */
export async function getBacklinks(noteId: string): Promise<LinkedNote[]> {
  const data = await apiClient.get<LinkedNotesResponse>(`note/${noteId}/backlinks`);
  return data.items;
}

/**
 * Fetches notes that the given note links TO (forward links).
 */
export async function getForwardLinks(noteId: string): Promise<LinkedNote[]> {
  const data = await apiClient.get<LinkedNotesResponse>(`note/${noteId}/forward-links`);
  return data.items;
}

/**
 * Fetches notes that mention the given note's title without an explicit [[…]] link.
 */
export async function getUnlinkedMentions(noteId: string): Promise<LinkedNote[]> {
  const data = await apiClient.get<LinkedNotesResponse>(`note/${noteId}/unlinked-mentions`);
  return data.items;
}

/**
 * Manually creates a link from sourceNoteId to targetNoteId.
 * Used when the user clicks "Link" on an unlinked mention.
 */
export async function createNoteLink(sourceNoteId: string, targetNoteId: string): Promise<void> {
  await apiClient.post(`note/${sourceNoteId}/link`, { targetNoteId });
}
